import { useState } from 'react';
import { useRouter } from 'next/router';
import LockIcon from 'components/shared/lockIcon/lockIcon';
import PaymentIcons from 'components/shared/paymentIcons/paymentIcons';
import SiteLink from 'components/shared/sitelink';
import OutOfStockPopup from 'components/account/cart/outOfStockPopup/outOfStockPopup';
import { useMultipass } from 'hooks';
import addCheckoutAttributes from 'lib/shopify/addCheckoutAttributes';
import { addLocaleToHref } from 'lib/utils/helpers';
import {
  getStorageCustomerSalonNo,
  getSelectedSalonNo,
  setSessionCheckoutUrl,
  getCheckoutIdByLocale,
  hasNewCustomerAccounts,
  getNewAccountsCheckoutUrl,
} from 'lib/shopify';
import CartSummary from '../cartSummary/cartSummary';
import styles from './cartFooter.module.scss';

const CartFooter = ({ data, locale, onClose }) => {
  const { cart, isEmpty, customer, colors, translations, links, media } = data;

  const router = useRouter();
  const [outOfStockLineItems, setOutOfStockLineItems] = useState(null);

  // Copied from cartPopup.jsx:134-165 — short-lived duplicate, host dies in Phase 2
  const startCheckout = async () => {
    const checkoutId = getCheckoutIdByLocale(locale);
    const affiliatedSalonNo = getStorageCustomerSalonNo(locale);
    const selectedSalonNo = getSelectedSalonNo(locale);
    const attributes =
      affiliatedSalonNo || selectedSalonNo
        ? [
            {
              key: 'salonNo',
              value:
                (Boolean(selectedSalonNo) && selectedSalonNo !== 'null' ? selectedSalonNo : null) ||
                affiliatedSalonNo,
            },
            { key: 'affiliatedSalonNo', value: affiliatedSalonNo || '' },
          ]
        : null;

    if (attributes) await addCheckoutAttributes(checkoutId, attributes, locale);

    if (customer?.isLoggedIn && hasNewCustomerAccounts(locale)) {
      document.location.href = await getNewAccountsCheckoutUrl(checkoutId, locale, cart.webUrl);
    } else if (customer?.isLoggedIn) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const url = await useMultipass(customer.email, cart.webUrl, locale);
      document.location.href = url;
    } else {
      // We save the checkout url so that we can redirect to it after login
      setSessionCheckoutUrl(locale, cart.webUrl);
      document.location.href = cart.webUrl;
    }
  };

  const handleCheckout = () => {
    const outOfStockItems = cart.lines?.edges?.filter(({ node }) =>
      node?.merchandise?.product.tags.includes('out-of-stock')
    );

    if (outOfStockItems.length) {
      setOutOfStockLineItems(outOfStockItems);
    } else {
      // view_cart now fires on drawer open (see cartDrawer.jsx); no longer here.
      startCheckout();
    }
  };

  return (
    <div className={styles.footer}>
      <CartSummary data={data} locale={locale} />
      {isEmpty ? (
        <SiteLink
          locale={locale}
          href={addLocaleToHref(links?.linkSlugEmptyCart?.slug, locale, router)}
          onClick={onClose}
        >
          <button className={styles.startShoppingButton} type="button">
            {translations?.cart?.startShoppingButtonText || ''}
          </button>
        </SiteLink>
      ) : (
        <button
          className={styles.checkout}
          style={{
            backgroundColor: colors?.checkoutButtonColor || '',
            color: colors?.checkoutButtonTextColor || '',
          }}
          type="button"
          onClick={handleCheckout}
        >
          <LockIcon color={colors?.checkoutButtonTextColor} width="2rem" height="2rem" />
          <span className={styles.checkoutContent}>{translations?.cart?.checkoutButtonText}</span>
        </button>
      )}

      <PaymentIcons
        className={styles.paymentMethods}
        icons={media?.cartPaymentIcons}
        iconHeight="3rem"
        iconWidth="5rem"
      />

      {translations?.cart?.paymentIconsText ? (
        <p className={styles.paymentText}>{translations.cart.paymentIconsText}</p>
      ) : null}

      {outOfStockLineItems?.length ? (
        <OutOfStockPopup
          lineItems={outOfStockLineItems}
          closeModal={() => setOutOfStockLineItems(null)}
          startCheckout={startCheckout}
          locale={locale}
          cart={cart}
        />
      ) : null}
    </div>
  );
};

export default CartFooter;
